// PageLoader — the branded, page-level loading mark (the LogoMark cube with a
// calm breathe). The richer alternative to the geometric <Spinner> for FULL-PAGE
// loads: app boot (ProtectedRoute waiting on /me), a cold route with nothing on
// screen yet. <Spinner> stays the in-control / inline loader (the 16px one in a
// Button, the list "refreshing" pulse).
//
// The cube breathes (opacity + scale), it does NOT spin — spinning a flat 2-D
// projection of the isometric cube breaks the 3-D form. Under reduced motion the
// global index.css block neutralizes the animation to a static mark; the
// role="status" + sr-only text carries the announcement, so it stays accessible
// without motion (WCAG 2.2 SC 4.1.3 + SC 2.3.3).

import { useTranslation } from 'react-i18next';
import { LogoMark } from './Logo.js';

export interface PageLoaderProps {
  /** Accessible loading text (sr-only). Defaults to the common loading string. */
  label?: string;
  /** Cube size in px. Default 56 for a full-page presence. */
  size?: number;
}

export function PageLoader({ label, size = 56 }: PageLoaderProps) {
  const { t } = useTranslation('common');
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-screen flex items-center justify-center"
    >
      <LogoMark
        size={size}
        aria-hidden
        className="animate-[brand-breathe_1.6s_ease-in-out_infinite] text-[var(--color-text-secondary)]"
      />
      <span className="sr-only">{label ?? t('statuses.loading')}</span>
    </div>
  );
}
